import moment from "moment";
import { Ref, ref } from "@vue/composition-api";
import * as api from "./api";
import { objectsSummary } from "@/common/api/summary.api";
import { POPULATION_TYPE } from "@/common/models";
import { FileSummary } from "@/common/models/entity-summary.model";
import { SharedState } from "@/common/state";
import { UseLoading } from "@/common/composition/loading";
import { ICONS } from "@/common/icons.map";
import { isNotEmpty } from "@/fp";

const can_cluster_be_displayed = ref(true);
const cluster_id = ref("");
const cluster_label: Ref<{ id: string; name: string } | null> = ref(null);
const time_frames: Ref<string[]> = ref([]);
const first_cluster_time = ref("");
const last_cluster_time = ref("");

const check_if_cluster_can_be_displayed = (summary: FileSummary): boolean => {
  const { firstExposer, lastExposer } = summary.clusterSummary;
  return isNotEmpty(firstExposer) && isNotEmpty(lastExposer);
};

const get_first_and_last_time = (summary: FileSummary): [string, string] => {
  const { firstExposer, lastExposer } = summary.clusterSummary;
  return [firstExposer.exposureTime, lastExposer.exposureTime];
};

const date_format = (f = "yyyy-MM-DD") => (the_date: string) => {
  return moment(the_date).format(f);
};

const file_labels = ref<{ id: string; name: string; icon: string }[]>([]);

const yyyy_mm_dd = date_format("yyyy-MM-DD");
const first_of_month = date_format("yyyy-MM-01");

const set_time_frames = (first_time: string, last_time: string) => {
  const first_frame = yyyy_mm_dd(first_time);
  const last_frame = yyyy_mm_dd(last_time);

  first_cluster_time.value = first_frame;
  last_cluster_time.value = last_frame;

  const from = first_of_month(first_frame);
  const to = first_of_month(last_frame);

  const dates = [from];
  let current = from;
  while (moment(current).isBefore(to)) {
    current = moment(current)
      .add(1, "month")
      .format("yyyy-MM-01");
    dates.push(current);
  }
  console.log("dates", dates);
  return dates;
};

const set_cluster_label = async (id: string, name: string) => {
  const res = await api.set_cluster_label({
    label_id: id,
    cluster_id: cluster_id.value,
  });
  if (res.success) {
    cluster_label.value = { id, name };
  }
};

const remove_cluster_label = async () => {
  const res = await api.remove_cluster_label(cluster_id.value);
  if (res.success) {
    cluster_label.value = null;
  }
};
const [is_loading, set_is_loading] = UseLoading(false).as_arr();

const get_labels_for_file = (
  info_types: { id: string; name: string }[] = []
): Array<{ id: string; name: string; icon: string }> => {
  if (info_types.length == 0) {
    const { id, labelName, code } = SharedState.default_label.value!;
    return [{ id, name: labelName, icon: ICONS.L0_ICONS.get(code) }];
  } else {
    return info_types
      .filter((item: { id: string; name: string }) =>
        SharedState.getInfoType(item.id)
      )
      .map((item: { id: string; name: string }) => {
        const label = SharedState.getInfoType(item.id);
        return {
          id: label.id,
          name: label.labelName,
          icon: ICONS.L0_ICONS.get(label.code),
        };
      });
  }
};

const init_cluster_data = async (file_id: string) => {
  set_is_loading(true);
  const res = await objectsSummary({
    ids: [file_id],
    type: POPULATION_TYPE.FILE,
  });
  if (res.success) {
    const summary = res.value[0] as FileSummary;

    can_cluster_be_displayed.value = check_if_cluster_can_be_displayed(summary);
    if (!can_cluster_be_displayed.value) {
      set_is_loading(false);
      return;
    }

    const [first_time_frame, last_time_frame] = get_first_and_last_time(
      summary
    );
    const {
      clusterSummary: { id },
      informationTypes,
    } = res.value[0] as FileSummary;
    file_labels.value = get_labels_for_file(informationTypes);

    cluster_id.value = id;
    const cluster_label_res = await api.get_cluster_label(cluster_id.value);
    if (cluster_label_res.success && cluster_label_res.value) {
      const cluster_label_id = cluster_label_res.value;
      const { labelName, id } = SharedState.allInfoTypes.value[
        cluster_label_id
      ];
      cluster_label.value = { id, name: labelName };
    } else {
      cluster_label.value = null;
    }
    time_frames.value = set_time_frames(first_time_frame, last_time_frame);
  }
  set_is_loading(false);
};

const clear = () => {
  cluster_id.value = "";
  cluster_label.value = null;
  time_frames.value = [];
  first_cluster_time.value = "";
  last_cluster_time.value = "";
};

export const cluster_state = {
  is_loading,
  clear,
  can_cluster_be_displayed,
  file_labels,
  init_cluster_data,
  cluster_label,
  remove_cluster_label,
  set_cluster_label,
  first_cluster_time,
  last_cluster_time,
};
